import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthSessionRevision } from 'src/auth/sessionEvents';
import { readWorkspaceSessionIdentity, workspaceSessionStamp } from 'src/auth/workspaceSession';
import { createQuoteEditorVersion, getQuoteEditorDraft, getQuoteEditorVersion, saveQuoteEditorDraft } from 'src/helper/backend_helper';
import { saveCreateQuoteDeviceDraft, loadCreateQuoteDeviceDraft, clearCreateQuoteDeviceDraft } from '../quoteSafety';

const pendingWorkingCopies = new Map<string, Promise<any>>();

export const editableAccountQuoteDraft = (snapshot: any) => {
  const inputs = { ...snapshot };
  delete inputs.adminFeeValue; delete inputs.discountValue;
  return inputs;
};
export const accountQuoteDraftContent = (snapshot: any) => JSON.stringify(editableAccountQuoteDraft(snapshot), (key, value) => {
  if (key === '_base') return undefined;
  return value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.keys(value).sort().map(field => [field, value[field]])) : value;
});
const content = accountQuoteDraftContent;
const commandId = () => `editor:${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
const message = (error: any) => error?.response?.data?.message ||
  (error?.message && !['Network Error', 'Failed to fetch', 'offline'].includes(error.message) ? error.message :
    'Your account draft could not be saved. Check your connection, keep this quote open, and choose Retry Saving.');
const sessionMessage = 'Your sign-in changed. Return to the original account to continue saving this quote.';

/** One account/event queue owns the optimistic revision; old responses cannot acknowledge newer input. */
const quoteDraftTransport = { getQuoteEditorDraft, getQuoteEditorVersion, createQuoteEditorVersion, saveQuoteEditorDraft };
export function useAccountQuoteDraft({ id, enabled, signature, capture, apply, editVersion,
  transport = quoteDraftTransport, scopeKey = 'quote', preserveCurrentOnRestore = false, persistentWorkingCopy = false }: {
  transport?: typeof quoteDraftTransport; scopeKey?: string; preserveCurrentOnRestore?: boolean; persistentWorkingCopy?: boolean;
  id: number | string; enabled: boolean; signature: string; capture: () => any;
  apply: (snapshot: any, canonical: any, requestKey: string) => any; editVersion: () => number;
}) {
  const { getQuoteEditorDraft, getQuoteEditorVersion, createQuoteEditorVersion, saveQuoteEditorDraft } = transport;
  useAuthSessionRevision();
  const owner = useRef(readWorkspaceSessionIdentity());
  const stamp = workspaceSessionStamp();
  const identity = `${scopeKey}:${id}:${stamp}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const callbacks = useRef({ capture, apply, editVersion });
  callbacks.current = { capture, apply, editVersion };
  const [state, setState] = useState<any>({ status: 'loading', versions: [], error: '', selected: '' });
  const [history, setHistory] = useState<any>(null);
  const [historyError, setHistoryError] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const historyRequest = useRef(0);
  const queue = useMemo(() => ({ state: { current: null as any }, acknowledged: { current: '' }, named: { current: '' },
    sequence: { current: Promise.resolve() as Promise<any> }, retry: { current: null as any },
    snapshot: capture(), flush: null as ((snapshot?: any) => Promise<any>) | null,
    pending: null as Promise<any> | null, pendingSignature: '' }), [identity]);
  queue.snapshot = capture();
  const stateRef = queue.state;
  const acknowledged = queue.acknowledged;
  const namedContent = queue.named;
  const initialSnapshot = useRef<any>(null);
  const sequence = queue.sequence;
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const epoch = useRef(0);
  const mounted = useRef(true);
  const retryCommand = queue.retry;
  const paused = useRef(false);
  const suspendedOwner = useRef<any>(null);
  const owns = (key = identity) => mounted.current && enabled && identityRef.current === key &&
    workspaceSessionStamp() === stamp && readWorkspaceSessionIdentity() === owner.current;
  const ownsAccount = () => enabled && workspaceSessionStamp() === stamp && readWorkspaceSessionIdentity() === owner.current;
  const recoveryScope = `account-editor:${scopeKey}:${id}:${owner.current}`;
  const remember = (snapshot: any) => {
    if (persistentWorkingCopy && ownsAccount() && stateRef.current && content(snapshot) === content(queue.snapshot)) saveCreateQuoteDeviceDraft({ snapshot,
      canonical: stateRef.current.canonical, retry: retryCommand.current }, recoveryScope);
  };
  const forget = (snapshot: any) => {
    if (persistentWorkingCopy && content(loadCreateQuoteDeviceDraft(recoveryScope)?.snapshot?.snapshot) === content(snapshot)) {
      clearCreateQuoteDeviceDraft(recoveryScope);
    }
  };
  const cancelTimer = () => { if (timer.current) clearTimeout(timer.current); };
  const update = (values: any) => { if (mounted.current) setState((previous: any) => ({ ...previous, ...values })); };

  useEffect(() => {
    if (!enabled) return;
    mounted.current = true;
    ++historyRequest.current; setHistory(null); setHistoryError(''); setHistoryLoading(false);
    const generation = ++epoch.current;
    if (suspendedOwner.current?.id !== String(id)) suspendedOwner.current = null;
    if (!owns() && !suspendedOwner.current) {
      suspendedOwner.current = { id: String(id), base: stateRef.current?.working?.snapshot ?? initialSnapshot.current };
    }
    const returningOwner = owns() ? suspendedOwner.current : null;
    stateRef.current = null;
    retryCommand.current = null;
    acknowledged.current = '';
    initialSnapshot.current = callbacks.current.capture();
    if (!owns()) {
      update({ status: 'unavailable', versions: [], error: sessionMessage, selected: '' });
      return () => { mounted.current = false; ++epoch.current; cancelTimer(); };
    }
    const retainWorkingCopy = () => { if (persistentWorkingCopy && ownsAccount()) void queue.flush?.(queue.snapshot); };
    if (persistentWorkingCopy) window.addEventListener('pagehide', retainWorkingCopy);
    const inputVersion = callbacks.current.editVersion();
    update({ status: 'loading', versions: [], error: '', selected: '' });
    const recoverWorkingCopy = async () => {
      await (pendingWorkingCopies.get(identity) ?? Promise.resolve()).catch(() => null);
      const recovery = loadCreateQuoteDeviceDraft(recoveryScope)?.snapshot;
      if (owns() && recovery?.retry?.version === false) {
        try {
          const receipt = await saveQuoteEditorDraft(id, recovery.retry.input);
          if (owns() && receipt?.data?.data?.working?.revision) forget(recovery.snapshot);
        } catch { /* Reconcile retained input against the current account revision below. */ }
      }
      return owns() ? getQuoteEditorDraft(id) : null;
    };
    const readDraft = persistentWorkingCopy
      ? recoverWorkingCopy()
      : getQuoteEditorDraft(id);
    void readDraft.then(response => {
      if (!owns() || generation !== epoch.current) return;
      const result = response?.data?.data;
      if (!result?.baseRevision) throw new Error('Account draft storage is unavailable. Keep this quote open and retry.');
      stateRef.current = result;
      const recovered = persistentWorkingCopy ? loadCreateQuoteDeviceDraft(recoveryScope)?.snapshot : null;
      const edited = Boolean(returningOwner) || callbacks.current.editVersion() !== inputVersion;
      if (edited) {
        callbacks.current.apply({ ...callbacks.current.capture(), _base: returningOwner?.base ?? initialSnapshot.current }, result.working?.snapshot ?? result.canonical, result.requestKey);
        suspendedOwner.current = null;
      } else if (recovered?.snapshot && content(recovered.snapshot) !== content(result.working?.snapshot)) {
        callbacks.current.apply({ ...recovered.snapshot, _base: recovered.canonical }, result.working?.snapshot ?? result.canonical, result.requestKey);
        acknowledged.current = content(result.working?.snapshot ?? result.initial ?? result.canonical);
        retryCommand.current = recovered.retry;
      } else if (result.working) {
        const applied = callbacks.current.apply(result.working.snapshot, result.canonical, result.requestKey);
        acknowledged.current = content(applied);
      } else if (!edited && result.initial) {
        acknowledged.current = content(callbacks.current.apply(result.initial, result.canonical, result.requestKey));
      } else if (!edited) acknowledged.current = content(callbacks.current.capture());
      if (recovered?.snapshot && content(recovered.snapshot) === content(result.working?.snapshot)) forget(recovered.snapshot);
      update({ status: edited || recovered?.snapshot ? 'pending' : 'saved', versions: result.versions, discardKey: result.discardKey, error: '', updatedAt: result.working?.updatedAt });
    }).catch(error => {
      if (owns() && generation === epoch.current) update({ status: 'unavailable', error: message(error) });
    });
    return () => {
      if (persistentWorkingCopy) window.removeEventListener('pagehide', retainWorkingCopy);
      retainWorkingCopy();
      mounted.current = false; ++epoch.current; cancelTimer();
    };
    // The identity includes the quote and stable account generation, never credentials.
  }, [identity, enabled]);

  const write = (snapshot: any, version: boolean, generation = epoch.current, afterUnmount = false) => {
    const operation = async () => {
      if (!(afterUnmount && persistentWorkingCopy ? ownsAccount() : owns()) || paused.current || (!afterUnmount && generation !== epoch.current)) return null;
      if (afterUnmount && content(snapshot) === acknowledged.current) { forget(snapshot); return null; }
      const current = stateRef.current;
      if (!current) throw new Error('Account draft storage is unavailable. Keep this quote open and retry.');
      if (owns()) update({ status: 'saving', error: '' });
      const snapshotSignature = content(snapshot);
      const retry = retryCommand.current;
      const input = retry?.signature === snapshotSignature && retry?.version === version ? retry.input : {
        revision: current.working?.revision ?? 0, baseRevision: current.baseRevision, requestKey: current.requestKey,
        snapshot: { ...snapshot, worksheetMembership: snapshot.worksheetMembership ? { ...snapshot.worksheetMembership, requestKey: current.requestKey } : undefined },
        commandId: commandId()
      };
      retryCommand.current = { signature: snapshotSignature, version, input };
      remember(snapshot);
      const response = await (version ? createQuoteEditorVersion(id, input) : saveQuoteEditorDraft(id, input));
      if (!owns() && !(persistentWorkingCopy && ownsAccount())) return null;
      const result = response?.data?.data;
      if (!result?.working?.revision) throw new Error('The server did not confirm this account save. Keep your edits open and retry.');
      // A same-quote restore drains this write and needs its revision. Other identities never do.
      stateRef.current = result;
      retryCommand.current = null;
      forget(snapshot);
      if (persistentWorkingCopy || generation === epoch.current) acknowledged.current = snapshotSignature;
      if (generation === epoch.current && owns()) {
        acknowledged.current = snapshotSignature;
        if (version) namedContent.current = snapshotSignature;
        update({ status: content(callbacks.current.capture()) === snapshotSignature ? 'saved' : 'pending',
          versions: result.versions, discardKey: result.discardKey, updatedAt: result.working.updatedAt,
          ...(version ? { selected: result.savedVersion?.key ?? '' } : {}) });
      }
      return response;
    };
    const queued = sequence.current.catch(() => null).then(operation);
    sequence.current = queued;
    return queued.catch(error => {
      if (owns() && generation === epoch.current) update({ status: error?.response?.status === 409 ? 'conflict' : 'error', error: message(error) });
      return null;
    });
  };

  useEffect(() => {
    cancelTimer();
    if (!owns() || !stateRef.current || ['loading', 'unavailable', 'error', 'conflict', 'restoring', 'paused'].includes(state.status)) return;
    if (content(callbacks.current.capture()) === acknowledged.current) return;
    remember(callbacks.current.capture());
    timer.current = setTimeout(() => void write(callbacks.current.capture(), false), 650);
    return cancelTimer;
  }, [identity, signature, state.status]);

  const flushWorking = (snapshot = callbacks.current.capture()): Promise<any> => {
    cancelTimer();
    if (!persistentWorkingCopy) return write(snapshot, false);
    if (!ownsAccount() || !stateRef.current || ['loading', 'unavailable', 'restoring'].includes(state.status)) return Promise.resolve(null);
    const signature = content(snapshot);
    if (queue.pending && queue.pendingSignature === signature) return queue.pending;
    remember(snapshot);
    const operation = write(snapshot, false, epoch.current, true);
    queue.pending = operation;
    queue.pendingSignature = signature;
    pendingWorkingCopies.set(identity, operation);
    void operation.then(() => {
      if (pendingWorkingCopies.get(identity) === operation) pendingWorkingCopies.delete(identity);
      if (queue.pending === operation) queue.pending = null;
    });
    return operation;
  };
  queue.flush = flushWorking;

  // Preview has its own request generation. It never drains, cancels, applies or
  // acknowledges the active editor's save queue.
  const viewVersion = async (key: string) => {
    if (!owns()) return;
    const request = ++historyRequest.current;
    setHistoryLoading(true); setHistoryError('');
    try {
      const source = (await getQuoteEditorVersion(id, key))?.data?.data;
      if (!owns() || request !== historyRequest.current) return;
      if (!source?.snapshot || !Array.isArray(source.snapshot.positions)) throw new Error('Missing snapshot');
      setHistory({ key, snapshot: source.snapshot });
    } catch {
      if (owns() && request === historyRequest.current) {
        setHistoryError('This version could not be loaded. Choose it again in Versions to retry; your active edits are retained.');
      }
    } finally {
      if (owns() && request === historyRequest.current) setHistoryLoading(false);
    }
  };
  const returnToActive = () => {
    ++historyRequest.current; setHistory(null); setHistoryError(''); setHistoryLoading(false);
  };
  const saveVersion = () => { cancelTimer(); return write(callbacks.current.capture(), true); };
  const restore = async (key: string) => {
    if (!owns() || paused.current) return null;
    cancelTimer();
    if (preserveCurrentOnRestore && content(callbacks.current.capture()) !== namedContent.current) {
      const preserved = await write(callbacks.current.capture(), true);
      if (!preserved) return null;
    }
    const generation = ++epoch.current;
    const inputVersion = callbacks.current.editVersion();
    update({ status: 'restoring', error: '' });
    await sequence.current.catch(() => null);
    if (!owns() || generation !== epoch.current) return null;
    try {
      const [sourceResponse, currentResponse] = await Promise.all([getQuoteEditorVersion(id, key), getQuoteEditorDraft(id)]);
      if (!owns() || generation !== epoch.current) return null;
      if (callbacks.current.editVersion() !== inputVersion) throw new Error('You edited the quote while the version was loading. Your current work is retained; choose the version again to restore it.');
      const source = sourceResponse?.data?.data;
      const latest = currentResponse?.data?.data;
      if (!source?.snapshot || !latest?.baseRevision || source.baseRevision !== latest.baseRevision || source.requestKey !== latest.requestKey) {
        throw new Error('The quote changed while this version was loading. Keep your current work and choose the version again.');
      }
      stateRef.current = latest;
      retryCommand.current = null;
      // Selecting a version is an explicit private restore, not recovery of a stale working copy.
      // Rebase its intended client values on the freshly checked canonical revision.
      const restoredSource = { ...source.snapshot,
        ...(source.snapshot._base ? { _base: latest.canonical } : {}),
        worksheetMembership: source.snapshot.worksheetMembership ? { ...source.snapshot.worksheetMembership, requestKey: latest.requestKey } : undefined,
        positions: source.snapshot.positions.map(row => scopeKey !== 'quote' && source.snapshot.worksheetMembership?.requestKey !== latest.requestKey
          ? { ...row, clientRequestDraftDecisions: [] } : row) };
      const snapshot = callbacks.current.apply(restoredSource, latest.canonical, latest.requestKey);
      namedContent.current = content(snapshot);
      update({ selected: key, versions: latest.versions, discardKey: latest.discardKey });
      return await write(snapshot, false, generation);
    } catch (error) {
      if (owns() && generation === epoch.current) update({ status: 'error', error: message(error) });
      return null;
    }
  };
  const retry = async () => {
    if (!owns()) return null;
    const generation = epoch.current;
    if (state.status === 'unavailable' || state.status === 'conflict') {
      try {
        const latest = (await getQuoteEditorDraft(id))?.data?.data;
        if (!owns() || generation !== epoch.current) return null;
        if (!latest?.baseRevision) throw new Error('The latest account draft could not be read. Keep your edits open and retry.');
        const local = { ...callbacks.current.capture(), _base: stateRef.current?.working?.snapshot ?? initialSnapshot.current };
        const snapshot = callbacks.current.apply(local, latest.working?.snapshot ?? latest.canonical, latest.requestKey);
        stateRef.current = latest;
        retryCommand.current = null;
        update({ versions: latest.versions, discardKey: latest.discardKey });
        return await write(snapshot, false, generation);
      } catch (error) {
        if (owns() && generation === epoch.current) update({ error: message(error) });
        return null;
      }
    }
    return write(callbacks.current.capture(), retryCommand.current?.version ?? false);
  };
  const runOperational = async <T,>(operation: () => Promise<T>, reconcileDraft = false): Promise<T> => {
    if (!enabled) return operation();
    if (!owns()) throw new Error(sessionMessage);
    cancelTimer();
    ++epoch.current;
    paused.current = true;
    update({ status: 'paused' });
    await sequence.current.catch(() => null);
    const priorCanonical = stateRef.current?.canonical;
    try {
      if (!owns()) throw new Error(sessionMessage);
      return await operation();
    } finally {
      try {
        if (!owns()) throw new Error(sessionMessage);
        const latest = (await getQuoteEditorDraft(id))?.data?.data;
        if (owns()) {
          if (!latest?.baseRevision) throw new Error('The saved quote revision could not be read. Keep your edits open and retry saving.');
          if (reconcileDraft) {
            callbacks.current.apply({ ...callbacks.current.capture(), _base: priorCanonical }, latest.canonical, latest.requestKey);
          }
          stateRef.current = latest;
          retryCommand.current = null;
          update({ status: 'pending', versions: latest.versions, discardKey: latest.discardKey, error: '' });
        }
      } catch (error) {
        if (owns()) update({ status: reconcileDraft ? 'conflict' : 'unavailable', error: message(error) });
        if (reconcileDraft) throw error;
      }
      finally { paused.current = false; }
    }
  };
  const refreshVersions = async () => {
    const generation = epoch.current;
    const latest = (await getQuoteEditorDraft(id))?.data?.data;
    if (owns() && generation === epoch.current && latest?.baseRevision) {
      update({ versions: latest.versions, discardKey: latest.discardKey });
    }
  };
  return { ...state, history, historyError, historyLoading, viewVersion, returnToActive, refreshVersions, saveVersion, flushWorking, restore, retry, runOperational, ready: owns() && Boolean(stateRef.current),
    hasUnsaved: enabled && content(capture()) !== acknowledged.current,
    invalidate: async () => { cancelTimer(); ++epoch.current; await sequence.current.catch(() => null); if (owns()) stateRef.current = null; } };
}
